import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../auth/SupabaseClient";
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Package,
  DollarSign,
  FileText,
  BarChart2,
  Calendar,
  Clock,
  Smartphone,
  Trash,
} from "lucide-react";

interface Size {
  id: string;
  name: string;
}

interface Category {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  sku: string;
  stock: number;
  category: Category | null;
  created_at: string;
  updated_at: string;
  size: Size | null;
  imageUrl?: string;
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [editedProduct, setEditedProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [sizes, setSizes] = useState<Size[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    fetchProduct();
    fetchSizes();
    fetchCategories();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: productData, error: productError } = await supabase
        .from("products")
        .select(
          `
          *,
          category:category_id (id, name),
          size:size_id (id, name)
        `
        )
        .eq("id", id)
        .single();

      if (productError) throw new Error(`Error fetching product: ${productError.message}`);
      if (!productData) throw new Error("Product not found");

      setProduct(productData);
      setEditedProduct(productData);
    } catch (error: any) {
      console.error("Error in fetchProduct:", error);
      setError(`An error occurred while fetching the product details: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSizes = async () => {
    try {
      const { data, error } = await supabase
        .from("sizes")
        .select("*")
        .order("name");
      if (error) throw error;
      setSizes(data);
    } catch (error: any) {
      console.error("Error fetching sizes:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("name");
      if (error) throw error;
      setCategories(data);
    } catch (error: any) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setEditedProduct((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;
    const selectedCategory = categories.find((cat) => cat.id === categoryId) || null;
    setEditedProduct((prev) => (prev ? { ...prev, category: selectedCategory } : null));
  };

  const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sizeId = e.target.value;
    const selectedSize = sizes.find((size) => size.id === sizeId) || null;
    setEditedProduct((prev) => (prev ? { ...prev, size: selectedSize } : null));
  };

  const handleSave = async () => {
    if (!editedProduct) return;

    try {
      const { error: updateError } = await supabase
        .from("products")
        .update({
          name: editedProduct.name,
          description: editedProduct.description,
          price: editedProduct.price,
          sku: editedProduct.sku,
          stock: editedProduct.stock,
          imageUrl: editedProduct.imageUrl,
          category_id: editedProduct.category?.id || null,
          size_id: editedProduct.size?.id || null,
        })
        .eq("id", editedProduct.id);

      if (updateError) throw updateError;

      setProduct(editedProduct);
      setEditMode(false);
      console.log("Update successful");
    } catch (error: any) {
      console.error("Error updating product:", error);
      setError(`An error occurred while updating the product: ${error.message}`);
    }
  };

  const DeleteProduct = async (productId: string) => {
    try {
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", productId);

      if (error) throw error;

      navigate("/dashboard/products");
    } catch (error: any) {
      console.error("Error deleting product:", error);
      setError(
        `An error occurred while deleting the product: ${error.message}`
      );
    }
  };

  const handleCancel = () => {
    setEditedProduct(product);
    setEditMode(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center p-4 bg-red-100 rounded-lg m-4">
        {error}
      </div>
    );
  }

  if (!product || !editedProduct) {
    return (
      <div className="text-center p-4 bg-yellow-100 text-yellow-700 rounded-lg m-4">
        Product not found
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg">
      <div className="mx-auto bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
            <button
              onClick={() => navigate("/dashboard/products")}
              className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition duration-150 ease-in-out mb-4 md:mb-0"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour aux produits
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-left">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {editedProduct.name}
              </h1>
            </div>
            <div className="flex space-x-2">
              {editMode ? (
                <>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 transition duration-150 ease-in-out"
                  >
                    <Save className="w-4 h-4 inline mr-2" />
                    Enregistrer
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400 transition duration-150 ease-in-out"
                  >
                    <X className="w-4 h-4 inline mr-2" />
                    Annuler
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 transition duration-150 ease-in-out"
                >
                  <Edit className="w-4 h-4 inline mr-2" />
                  Modifier
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Product Details */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Informations du produit
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nom
                </label>
                {editMode ? (
                  <input
                    type="text"
                    name="name"
                    value={editedProduct.name}
                    onChange={handleInputChange}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-600 dark:text-gray-300">{editedProduct.name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                {editMode ? (
                  <textarea
                    name="description"
                    value={editedProduct.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900  dark:text-gray-100 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-600 dark:text-gray-300">{editedProduct.description}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Catégorie
                </label>
                {editMode ? (
                  <select
                    name="category_id"
                    value={editedProduct.category?.id || ""}
                    onChange={handleCategoryChange}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sélectionnez une catégorie</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-600 dark:text-gray-300">
                    {editedProduct.category?.name || "Aucune catégorie sélectionnée"}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Taille
                </label>
                {editMode ? (
                  <select
                    name="size_id"
                    value={editedProduct.size?.id || ""}
                    onChange={handleSizeChange}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sélectionnez une taille</option>
                    {sizes.map((size) => (
                      <option key={size.id} value={size.id}>
                        {size.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-600 dark:text-gray-300">
                    {editedProduct.size?.name || "Aucune taille sélectionnée"}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2" />
              Prix et inventaire
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Prix
                </label>
                {editMode ? (
                  <input
                    type="number"
                    name="price"
                    value={editedProduct.price}
                    onChange={handleInputChange}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-600 dark:text-gray-300">
                    {editedProduct.price} €
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SKU
                </label>
                {editMode ? (
                  <input
                    type="text"
                    name="sku"
                    value={editedProduct.sku}
                    onChange={handleInputChange}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-600 dark:text-gray-300">{editedProduct.sku}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quantité en stock
                </label>
                {editMode ? (
                  <input
                    type="number"
                    name="stock"
                    value={editedProduct.stock}
                    onChange={handleInputChange}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-600 dark:text-gray-300">{editedProduct.stock}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
            <BarChart2 className="w-5 h-5 mr-2" />
            Informations supplémentaires
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-gray-500" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-medium">Créé le :</span>{" "}
                {new Date(editedProduct.created_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center">
              <Clock className="w-5 h-5 mr-2 text-gray-500" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-medium">Dernière mise à jour :</span>{" "}
                {new Date(editedProduct.updated_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Product Image */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
            <Smartphone className="w-5 h-5 mr-2" />
            Image du produit
          </h2>
          <div className="flex justify-center">
            {editedProduct.imageUrl ? (
              <img
                src={editedProduct.imageUrl}
                alt={editedProduct.name}
                className="max-w-full h-auto rounded-lg shadow-lg"
                style={{ maxHeight: "300px" }}
              />
            ) : (
              <div
                className="bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center"
                style={{ width: "300px", height: "300px" }}
              >
                <p className="text-gray-500 dark:text-gray-400">
                  Aucune image disponible
                </p>
              </div>
            )}
          </div>
          {editMode && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                URL de l'image
              </label>
              <input
                type="text"
                name="imageUrl"
                value={editedProduct.imageUrl || ""}
                onChange={handleInputChange}
                className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Entrez l'URL de l'image"
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
          <button
            onClick={() => {
              if (
                window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")
              ) {
                if (!id) return;
                DeleteProduct(id);
              }
            }}
            className="px-4 py-2 bg-red-500 text-white rounded-md text-sm font-medium hover:bg-red-600 transition duration-150 ease-in-out"
          >
            <Trash className="w-4 h-4 inline mr-2" />
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}
